import type { ClientListRow } from "../../../server/core/clients";
import type { PageInfo } from "../../../server/rules/list-page";
import { isClientModule, type ClientModule } from "../../../server/rules/clients";
import type { ScreenContext } from "../../shell/types";
import { api, el } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { firstPageParams, list, type Column } from "../../ui/list";
import { openModal } from "../../ui/modal";
import { pageHead } from "../empty";
import { clientForm, endsCell, refuse } from "./forms";
import { clientRecord } from "./record";

// Each module's Clients screen (brief 1B-1, mockup Staffing, Home Care and Transportation ›
// Clients): the list of real clients with stage chips, a filter by client type, the contract's
// end with a warning when it's near, and New client in a modal. A row opens the client record.
// A client used by two modules is the same record in both lists.

/** The line under each list's title (mockup). */
const LINES: Record<ClientModule, string> = {
  staffing: "Facilities, clinics, practices and the company's own businesses.",
  homecare: "Who pays and how. Each client keeps its own setup.",
  transport: "Who orders trips and how they pay.",
};

/** The mockup's stage chip: the first stage plain, the last done, the rest in progress. */
export function stageChip(
  r: Pick<ClientListRow, "stage" | "stageName" | "stageLast">,
): HTMLElement {
  const kind = r.stageLast ? "ok" : r.stage === 0 ? "plain" : "info";
  return el("span", { class: `chip ${kind}` }, r.stageName);
}

export async function clientsScreen(host: HTMLElement, ctx: ScreenContext) {
  const module = ctx.section.id;
  if (!isClientModule(module)) return;
  if (ctx.record) return clientRecord(host, ctx, module, ctx.record);
  return clientList(host, ctx, module);
}

async function clientList(host: HTMLElement, ctx: ScreenContext, module: ClientModule) {
  type Answer = PageInfo & {
    clients: ClientListRow[];
    stages: string[];
    canEdit: boolean;
    homecareTypes: { id: string; name: string }[];
    /** The module's client types, from the company's rows (brief 1D-6). */
    clientTypes: string[];
  };
  // Brief 1E-32: the server answers one page at a time.
  const ask = (params: URLSearchParams) =>
    api<Answer>("GET", `/api/clients?module=${module}&${params.toString()}`);
  const listId = `${module}/clients`;
  const res = await ask(firstPageParams(listId, ctx.me.user.id));
  const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " New client");
  add.addEventListener("click", () =>
    newClient(
      ctx,
      module,
      res.ok ? res.body.homecareTypes : [],
      res.ok ? res.body.clientTypes : [],
    ),
  );
  host.append(pageHead(ctx, LINES[module], res.ok && res.body.canEdit ? [add] : []));
  if (!res.ok) {
    host.append(el("p", { class: "note", role: "alert" }, "The clients couldn't be loaded."));
    return;
  }
  const types = [
    ...new Set(
      module === "homecare" ? res.body.homecareTypes.map((t) => t.name) : res.body.clientTypes,
    ),
  ].filter(Boolean);
  const columns: Column<ClientListRow>[] = [
    {
      id: "name",
      label: "Client",
      text: (r) => r.name,
      search: true,
      cell: (r) =>
        el(
          "span",
          {},
          el("b", {}, r.name),
          r.kind !== "client" ? el("div", { class: "small muted" }, r.kindLabel) : null,
        ),
    },
    {
      id: "type",
      label: "Type",
      text: (r) => r.clientType ?? "–",
      values: types,
      search: true,
    },
    {
      id: "ends",
      label: "Contract ends",
      text: (r) => r.contractEnds ?? "–",
      cell: (r) => endsCell(r.contractEnds, r.warning),
    },
    {
      id: "stage",
      label: "Stage",
      text: (r) => r.stageName,
      cell: (r) => stageChip(r),
      values: res.body.stages,
    },
  ];
  host.append(
    list<ClientListRow>({
      id: listId,
      userId: ctx.me.user.id,
      noun: "clients",
      first: { rows: res.body.clients, page: res.body },
      source: async (params) => {
        const r = await ask(params);
        return r.ok ? { rows: r.body.clients, page: r.body } : null;
      },
      rowId: (r) => r.id,
      columns,
      chips: { values: res.body.stages, of: (r) => r.stageName },
      onOpen: (r) => ctx.go(`/${module}/clients/${r.id}`),
      empty: "No clients yet.",
    }),
  );
}

/** New client, in a modal (brief 1B-1); the new record opens when it's made. */
function newClient(
  ctx: ScreenContext,
  module: ClientModule,
  homecareTypes: readonly { id: string; name: string }[],
  types: readonly string[],
) {
  const on = ctx.me.modulesOn.filter(isClientModule);
  const form = clientForm(module, on, module === "homecare" ? { homecareTypes } : { types });
  const create = el("button", { type: "submit", class: "btn pri" }, icon("plus"), " Create client");
  const body = el("form", { class: "form", style: "max-width:none", novalidate: "" }, form.el);
  const m = openModal({
    title: "New client",
    sub: "Name it and pick its type. Everything can be changed after.",
    body: [body],
    actions: [create],
    wide: true,
  });
  form.focus();
  const submit = async () => {
    create.disabled = true;
    const res = await api<{ client: { id: string } }>("POST", "/api/clients", form.values());
    create.disabled = false;
    if (!res.ok) return refuse(m.note, res.body.error);
    m.close();
    ctx.go(`/${module}/clients/${res.body.client.id}`);
  };
  body.addEventListener("submit", (e) => {
    e.preventDefault();
    void submit();
  });
  create.addEventListener("click", (e) => {
    e.preventDefault();
    void submit();
  });
}
